import { createContext, useState, useEffect, useContext } from "react";
import { jwtDecode } from "jwt-decode";
import { requestDevs, requestIdToken, requestLogout, requestPulseCheck, requestRefresh } from "../requests/users_requests";
import { useLocation, useNavigate } from "react-router-dom";
import { getAccessToken, getIdToken, removeAccessToken, removeIdToken, setAccessToken, setIdToken } from "../utils/authenticationUtils";
import { loadingActive } from "../components/Background";
import { useTranslation } from "react-i18next";
import { languageOptions } from "../entries";
import LanguageDetector from 'i18next-browser-languagedetector';
import { detectionOptions } from "../components/LanguageComponents";
import { devLog, devWarn } from "../utils/loggerUtils";
import { requestChangeLanguagePreference, requestGetUserPreferences } from "../requests/profile_requests";

const getLocalItem = (id, defaultValue = null) => {
    const localItem = localStorage.getItem(id)

    if (localItem) {
        return localItem
    }
    else {
        return defaultValue
    }
}



const AccountContext = createContext();


function AccountController(props) {

    const [user, setUser] = useState(null);
    const [devs, setDevs] = useState([]);
    const [loading, setLoading] = useState(true)
    const [isTempUser, setIsTempUser] = useState(false)
    const [language, setLanguage] = useState(null);
    const { i18n } = useTranslation()
    const { t } = useTranslation('common')
    const location = useLocation()
    const [userPreferences, setUserPreferences] = useState(null)

    const getDefaultLanguage = () => {
        const languageDetector = new LanguageDetector(null, detectionOptions);
        // const browserLanguage = navigator.languages && navigator.languages[0].split("-")[0] || navigator.language && navigator.language.split("-")[0] || languageDetector.detect() || 'en'
        // if (Object.keys(languageOptions).includes(browserLanguage)) {
        //     return browserLanguage
        // }
        // else {
        //     return "en"
        // }
        const browserLanguage =
            (navigator.languages && navigator.languages[0]) || // Full language code
            (navigator.language) ||
            languageDetector.detect() ||
            'en';

        const baseLanguage = browserLanguage.split("-")[0]; // Extract base language
        // const region = browserLanguage.split("-")[1]; // Extract region, if present
        if (Object.keys(languageOptions).includes(browserLanguage)) {
            return browserLanguage; // Return full language code if it matches
        } else if (Object.keys(languageOptions).includes(baseLanguage)) {
            return baseLanguage; // Fallback to base language
        }
        console.warn("Something went wrong when loading language. Returning to default.")
        return 'en'; // Fallback to base language
    }


    useEffect(() => {
        let languagePreference = getDefaultLanguage()
        if (!user) {
            languagePreference = localStorage.getItem("anon" + "_languagePreference")
        }
        else if (user.language) {
            languagePreference = user.language
            devLog("Loading language preference according to db:", user.language)
        }
        else {
            languagePreference = localStorage.getItem(user.id + "_languagePreference")
            if (!languagePreference) {
                languagePreference = localStorage.getItem("anon" + "_languagePreference")
            }
        }
        if (languagePreference) {
            setLanguage(languagePreference)
        }
        else {
            setLanguage(getDefaultLanguage())
        }
        if (user?.class === "dev") {

            requestGetUserPreferences({})
                .then((data) => {
                    if (user.id === data.user_preferences.user_id) {
                        delete data.user_preferences.user_id
                        setUserPreferences(data.user_preferences)
                    }
                    else {
                        setUserPreferences(null)
                    }
                })
                .catch((error) => {
                    console.error(error.status, error.message)
                    setUserPreferences(null)
                })
        }
    }, [user])

    useEffect(() => {
        if (language) {
            document.documentElement.setAttribute('dir', language === 'ar' ? 'rtl' : 'ltr');
        }
    }, [language])

    useEffect(() => {
        if (user && !user.language && language) {
            requestChangeLanguagePreference({ language: language })
                .then((data) => {
                    let prevUser = user
                    prevUser.language = language
                    setUser(prev => prevUser)
                })
                .catch((error) => {
                    console.error(error.status, error.message)
                })
        }
    }, [user, language])

    if (i18n.resolvedLanguage !== language) {
        i18n.changeLanguage(language).then(() => {
            devLog('Language updated to:', i18n.resolvedLanguage);
        });
    }

    const navigator = useNavigate()

    useEffect(() => {

        if (devs.length === 0) {
            return
        }
        // if (user && Object.keys(devs).includes(user.id)) {
        //     setUser(devs[user.id])
        // }


    }, [devs])

    useEffect(() => {
        if (user) {

            if (devs[user.id] !== user) {
                setDevs((prev) => { return { ...prev, [user.id]: user } })
            }
        }
    }, [user])


    const onLoginOAuth2 = (accessToken, idToken) => {
        const decodedAccessToken = jwtDecode(accessToken)
        const decodedIdToken = jwtDecode(idToken)
        setUser({ ...decodedAccessToken, ...decodedIdToken })
    }

    const onLogoutOAuth2 = () => {
        loadingActive(true)
        requestLogout({}).then(() => {
            removeAccessToken()
            removeIdToken()
            setUser(null)
            loadingActive(false)
            navigator("/login")
        }).catch((error) => {
            console.error("Error while logging out:", error)
            loadingActive(false)
        })
    }


    const getUserOAuth2 = () => {
        // setUser(prev => ({ ...prev, ...userData }))
        const decodedAccessToken = jwtDecode(getAccessToken().access_token)
        const decodedIdToken = jwtDecode(getIdToken())
        setUser(prev => ({ id: decodedAccessToken.sub, ...decodedIdToken }))
    }


    const getSessionOAuth2 = async () => {
        const accessTokenItem = getAccessToken()
        const accessToken = accessTokenItem.access_token

        let userId = null
        if (accessToken) {
            try {
                const decoded = jwtDecode(accessToken)
                const currentTime = Date.now() / 1000
                const utcTimeDecoded = new Date(decoded.exp * 1000)
                const utcTimeCurrentTime = new Date(currentTime * 1000)

                if (decoded.exp < currentTime) {
                    devLog("Access token expired. Trying to refresh.")

                    requestRefresh({}).then((data) => {
                        // Success
                        devLog("Access token refreshed.")
                        setAccessToken(data.access_token)
                        getUserOAuth2({ id: data.access_token.access_token.sub })
                        setIdToken(data.id_token)
                        getUserOAuth2()
                        userId = data.access_token.access_token.sub
                        loadingActive(false)
                        setLoading(false)

                    }).catch((error) => {
                        console.error(error)
                        loadingActive(false)
                        setLoading(false)
                        navigator("/login")
                    })

                } else {
                    // Success
                    getUserOAuth2()
                    loadingActive(false)
                    setLoading(false)
                    userId = decoded.sub
                }
            } catch (error) {
                console.error("Invalid access token. Redirecting to login.", accessToken, error);
                removeAccessToken()
                removeIdToken()
                loadingActive(false)
                setLoading(false)
                navigator("/login"); // Navigate to login page
            }
        } else {
            devLog("No access token found. Redirecting to login.");
            loadingActive(false)
            setLoading(false)
            navigator("/create-ticket"); // Navigate to login page 
        }

        if (!userId) {
            return;
        }
        const idToken = getIdToken()
        if (idToken && idToken !== "undefined") {
            try {
                const decoded = jwtDecode(idToken)

                getUserOAuth2()
                loadingActive(false)
                setLoading(false)

            } catch (error) {
                console.error("Invalid id token. Requesting a new one.", error);
                removeIdToken()
                requestIdToken({ userId: userId }).then((data) => {
                    setIdToken(data)
                    getUserOAuth2()
                    loadingActive(false)
                    setLoading(false)
                })
            }
        } else {
            devLog("No id token found. Requesting.");
            requestIdToken({ userId: userId }).then((data) => {
                setIdToken(data)
                getUserOAuth2()
                loadingActive(false)
                setLoading(false)
            })
        }

    }


    const onLogout = () => {
        loadingActive(true)
        requestLogout({}).then(() => {
            navigator("/login")
            setUser(null)
            loadingActive(false)
        }).catch((error) => {
            console.error("Error while logging out:", error)
            loadingActive(false)
        })
    }

    const getUserFromIdToken = (idToken) => {
        const userData = jwtDecode(idToken)
        setUser(userData)
        return userData
    }

    const getSession = async () => {

        return requestPulseCheck({}).then((data) => {
            if (data) {
                setIsTempUser(!!data.temp)
                let userData = getUserFromIdToken(data.id_token)
                loadingActive(false)
                setLoading(false)
                return userData.class === "dev"
            }
        })
            .catch((error) => {
                console.error(error.status, "Error during pulse check:", error.message)
                if (location.pathname.startsWith("/member/ticket/")) {
                    let ticketCode = location.pathname.split("/")[location.pathname.split("/").length - 1]
                    window.location.href = `https://mobile2.global/home?ticket_code=${ticketCode}`
                }
                loadingActive(false)
                setLoading(false)
                if (window.location.pathname.startsWith("/ticket/") || window.location.pathname.startsWith("/setup-password/") || window.location.pathname.startsWith("/login")) {
                    devLog("in user ticket")
                    return true
                }
                navigator("/home")
            })
    }


    const checkSession = async () => {
        return requestPulseCheck({}).then((data) => {
            setIsTempUser(!!data.temp)
            if (data.temp) {
                return false
            }
            return {
                "user_type": data.user_type
            }
        }).catch((error) => {
            if (error.status === 401) {
                devWarn("No session found")
                // navigator("/create-ticket")
            }
            return false
        })
    }

    const getDevs = () => {
        requestDevs({ verbatim: true })
            .then((data) => {
                let _devs = {}
                for (let i = 0; i < data.devs.length; i++) {
                    let dev = data.devs[i]
                    _devs[dev.id] = dev
                }
                setDevs(prev => ({ ..._devs }))
            })
            .catch((error) => {
                console.error(error.status, error.message)
            })
    }


    useEffect(() => {
        if (!user) {
            loadingActive(true)
            getSession().then((isDev) => {
                // navigator(currentPath)
                if (isDev) {
                    getDevs()
                }

            }).catch((error) => {
                console.error("Error while getting session:", error)
                loadingActive(false)
                setLoading(false)
                navigator("/login")
            })
        }

    }, [])


    useEffect(() => {
        if (loading) {
            console.error("Failed to load account info.")
        }
        else {
            console.info("Account info loaded.")
        }
    }, [loading])

    if (loading) {
        return <div className="text-center content-center h-screen">{t("Failed to load account info.")}</div>
    }

    return (
        <AccountContext.Provider
            value={{
                user,
                setUser,
                devs,
                setDevs,
                getUserFromIdToken,
                checkSession,
                onLogout,
                isTempUser,
                setIsTempUser,
                language,
                setLanguage,
                getDevs,
                userPreferences,
                setUserPreferences,
            }}>
            {props.children}
        </AccountContext.Provider>
    )
}


export { AccountContext, AccountController };